/** Fetch wrapper — 쿠키 기반 세션, 401 시 로그인 화면 트리거.
 *
 * 운영: Nginx 가 /arkiveai-admin/api/* → backend /api/* 매핑. SPA 도 /arkiveai-admin 하위에 위치.
 *      → 동일 도메인 → credentials: 'include' 로 쿠키 자동 송신.
 * 개발: Vite proxy 가 /api → http://localhost:8080 으로 포워드.
 */
import type { ApiError } from "./types";

export class HttpError extends Error implements ApiError {
  status: number;
  code?: string;
  constructor(status: number, message?: string, code?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.code = code;
  }
}

const BASE = "/api";

/** 인증 콜백 — 401 발생 시 호출. AuthContext 가 등록. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  query?: Record<string, string | number | boolean | undefined | null>;
  json?: unknown;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { query, json, headers, ...rest } = opts;
  const init: RequestInit = {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    ...rest,
  };
  if (json !== undefined) init.body = JSON.stringify(json);

  const res = await fetch(buildUrl(path, query), init);

  if (res.status === 401) {
    onUnauthorized?.();
    throw new HttpError(401, "인증이 필요합니다", "UNAUTHENTICATED");
  }
  if (!res.ok) {
    let code: string | undefined;
    let message: string | undefined;
    try {
      const body = await res.json();
      code = body?.detail?.code ?? body?.code;
      message = body?.detail?.message ?? body?.message;
    } catch {
      // ignore
    }
    throw new HttpError(res.status, message, code);
  }
  if (res.status === 204) return undefined as T;
  // PDF/Excel/CSV 등 non-JSON 은 호출 측에서 별도 fetchBlob 사용
  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string, query?: RequestOptions["query"]) {
    return request<T>(path, { method: "GET", query });
  },
  post<T>(path: string, json?: unknown, query?: RequestOptions["query"]) {
    return request<T>(path, { method: "POST", json, query });
  },
  delete<T>(path: string, query?: RequestOptions["query"]) {
    return request<T>(path, { method: "DELETE", query });
  },
  patch<T>(path: string, json?: unknown) {
    return request<T>(path, { method: "PATCH", json });
  },
};

/** 다운로드용 — Blob 반환. CSV / Excel / PDF 사용. */
export async function fetchBlob(
  path: string,
  query?: RequestOptions["query"],
): Promise<Blob> {
  const res = await fetch(buildUrl(path, query), {
    credentials: "include",
  });
  if (res.status === 401) {
    onUnauthorized?.();
    throw new HttpError(401, "인증 필요", "UNAUTHENTICATED");
  }
  if (!res.ok) throw new HttpError(res.status);
  return res.blob();
}

/** 브라우저에서 Blob 다운로드 트리거. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** 로그인 진입 — 풀 페이지 리다이렉트. 백엔드가 pilgrims/NW 로그인으로 302. */
export function redirectToLogin(redirectTo?: string) {
  const target = redirectTo ?? window.location.pathname + window.location.search;
  const url = `${BASE}/auth/login?redirect_to=${encodeURIComponent(target)}`;
  window.location.href = url;
}

export interface AuthConfig {
  auth_mode: "pilgrims" | "nw" | "disabled";
  login_url: string;
  logout_url: string;
}

/** 완전 로그아웃 — ArkiveAI 세션 파기 후, pilgrims 모드면 공유 쿠키까지 만료.
 *  pilgrims 공유 쿠키(pilgrims_auth)는 로컬 세션만 지워선 남아 자동 재로그인되므로
 *  logout_url 로 이동해 만료시킨다. pilgrims 가 아니면 로그인 화면으로. */
export async function performLogout(): Promise<void> {
  try {
    await api.post<void>("/auth/logout");
  } catch {
    /* 이미 만료 등 — 무시 */
  }
  let cfg: AuthConfig | null = null;
  try {
    cfg = await api.get<AuthConfig>("/auth/config");
  } catch {
    /* config 조회 실패 — 로컬 로그아웃만 */
  }
  if (cfg?.auth_mode === "pilgrims" && cfg.logout_url) {
    const returnTo = `${window.location.origin}/`;
    const sep = cfg.logout_url.includes("?") ? "&" : "?";
    window.location.href = `${cfg.logout_url}${sep}return=${encodeURIComponent(returnTo)}`;
    return;
  }
  redirectToLogin("/");
}
